<script setup lang="ts">
// #836: ライセンスは本文ではなく**バッジ**で出す。
//
// GitHub / npm / crates.io と同じ流儀で、短い名前を pill にする。バッジ自体を
// 条文へのリンクにするのは Creative Commons の公式案内でもある。色は「義務の
// 重さ」の 3 段階で、サーバの `LICENSE_BADGES` が決める（フロントで判定しない）。
//
// `note` は素材ごとの個別事情（「音楽：魔王魂 と表記すること」等）。共通の
// ライセンス名を汚さずに、バッジの下へ 1 行で出す。
import { mdiOpenInNew } from '@mdi/js'

interface Props {
  badge?: string
  tone?: 'free' | 'credit' | 'limited'
  url?: string
  note?: string
}

const props = withDefaults(defineProps<Props>(), {
  badge: '不明',
  tone: 'limited',
})

// `/licenses/<slug>` は内部ページ、それ以外は外部の条文。
const isInternal = computed(() => Boolean(props.url?.startsWith('/')))
</script>

<template>
  <span class="license-badge-wrap">
    <NuxtLink
      v-if="url && isInternal"
      :to="url"
      class="license-badge"
      :class="`license-badge--${tone}`"
    >{{ badge }}</NuxtLink>
    <a
      v-else-if="url"
      :href="url"
      target="_blank"
      rel="noopener noreferrer"
      class="license-badge"
      :class="`license-badge--${tone}`"
    >{{ badge }}<v-icon
      :icon="mdiOpenInNew"
      size="9"
      class="license-badge__external"
      aria-hidden="true"
    /></a>
    <span
      v-else
      class="license-badge"
      :class="`license-badge--${tone}`"
    >{{ badge }}</span>

    <span
      v-if="note"
      class="license-badge__note"
    >{{ note }}</span>
  </span>
</template>

<style scoped>
.license-badge-wrap {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}

.license-badge {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 1px 7px;
  border-radius: 9px;
  border: 1px solid;
  font-size: 0.7rem;
  line-height: 1.5;
  white-space: nowrap;
  text-decoration: none;
  font-weight: 600;
}

a.license-badge:hover,
.license-badge:hover {
  filter: brightness(0.94);
}

/* 制約が軽い（CC0 / フリー） */
.license-badge--free {
  background: rgba(76, 140, 74, 0.14);
  border-color: rgba(76, 140, 74, 0.45);
  color: #3d6f3b;
}

/* クレジット表記が要る（CC BY / OFL / MIT / 要クレジット） */
.license-badge--credit {
  background: rgba(191, 133, 30, 0.16);
  border-color: rgba(191, 133, 30, 0.5);
  color: #8a5f14;
}

/* C&M 内のみ / 未決定 / 不明 */
.license-badge--limited {
  background: rgba(90, 80, 70, 0.12);
  border-color: rgba(90, 80, 70, 0.35);
  color: #5c5148;
}

.license-badge__external {
  opacity: 0.7;
}

.license-badge__note {
  font-size: 0.68rem;
  color: rgba(60, 40, 20, 0.72);
  text-align: right;
  max-width: 22em;
}
</style>
